import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Pencil,
  Lock,
  ShieldCheck,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { DynamicField } from "@/components/DynamicField";
import { api, RON, track, saveOrderToken } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const DocPreview = ({ s }) => {
  const paragraphs = (s.context || "")
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return (
    <div className="relative" data-testid="document-preview-body">
      <span className="pointer-events-none select-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <span className="rotate-[-28deg] text-4xl sm:text-6xl font-black text-slate-900/[0.06] dark:text-white/[0.06] tracking-widest whitespace-nowrap">
          PREVIZUALIZARE
        </span>
      </span>
      <div className="relative text-[13px] leading-relaxed text-slate-800 dark:text-slate-100" style={{ fontFamily: "Georgia, serif" }}>
        <p className="font-bold">{s.sender_name}</p>
        {s.sender_lines?.map((l, i) => (
          <p key={i} className="text-slate-500 text-xs">{l}</p>
        ))}
        {s.recipient_lines?.length > 0 && (
          <div className="mt-4 text-right">
            <p className="font-semibold">Către,</p>
            {s.recipient_lines.map((l, i) => (
              <p key={i}>{l}</p>
            ))}
          </div>
        )}
        <p className="mt-3 text-right">Data: {s.date}</p>
        <p className="mt-5 text-center font-bold uppercase tracking-wide">{s.title}</p>
        <div className="mx-auto mt-1 mb-4 h-0.5 w-16 bg-blue-600" />
        {paragraphs.length > 0 ? (
          paragraphs.map((p, i) => (
            <p key={i} className="mt-2 text-justify whitespace-pre-wrap">{p}</p>
          ))
        ) : (
          <p className="mt-2 text-slate-400 italic">
            Conținutul va fi formulat pe baza informațiilor introduse.
          </p>
        )}
        {s.deadline && (
          <p className="mt-2 text-justify"><strong>Termen solicitat:</strong> {s.deadline}</p>
        )}
        <p className="mt-3">{s.incheiere}</p>
        <p className="mt-6">Cu stimă,</p>
        <p className="mt-4 font-bold">{s.sender_name}</p>
        <p className="text-slate-400 mt-1">Semnătura: _______________________</p>
      </div>
    </div>
  );
};

export default function GenerateFlow() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [step, setStep] = useState(1);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useSEO(`Generează ${doc?.title || "document"} | ActeSimplu`);

  useEffect(() => {
    api
      .get(`/documents/${slug}`)
      .then((r) => {
        setDoc(r.data);
        const init = {};
        r.data.fields.forEach((f) => {
          if (f.name === "data_document")
            init[f.name] = new Date().toISOString().slice(0, 10);
        });
        setValues(init);
      })
      .catch(() => navigate("/"));
  }, [slug]);

  const onChange = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const emailField = doc?.fields.find((f) => f.type === "email");

  const validate = () => {
    const errs = {};
    doc.fields.forEach((f) => {
      const val = values[f.name];
      if (f.required && (!val || String(val).trim() === "")) {
        errs[f.name] = "Acest câmp este obligatoriu.";
      } else if (f.type === "email" && val && !EMAIL_RE.test(val)) {
        errs[f.name] = "Adresa de email nu este validă.";
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const toReview = async () => {
    if (!validate()) {
      toast.error("Te rugăm să completezi corect câmpurile obligatorii.");
      return;
    }
    track("form_completed", { slug });
    setStep(2);
    window.scrollTo(0, 0);
    setPreviewLoading(true);
    try {
      const r = await api.post("/preview", { slug, form_data: values });
      setPreview(r.data.sections);
    } catch (e) {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const pay = async () => {
    if (!agree) {
      toast.error("Trebuie să confirmi că ai verificat datele și ești de acord.");
      return;
    }
    setSubmitting(true);
    try {
      const email = emailField ? values[emailField.name] : values.expeditor_email;
      const orderRes = await api.post("/orders", {
        slug,
        email,
        form_data: values,
      });
      const { order_id, access_token } = orderRes.data;
      saveOrderToken(order_id, access_token);
      track("checkout_started", { slug, order_id });
      const checkoutRes = await api.post(`/orders/${order_id}/checkout`, {
        access_token,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.checkout_url;
    } catch (e) {
      setSubmitting(false);
      const msg = e?.response?.data?.detail || "A apărut o eroare. Încearcă din nou.";
      toast.error(msg);
    }
  };

  if (!doc) {
    return (
      <div className="flex items-center justify-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const filledFields = doc.fields.filter((f) => values[f.name] && String(values[f.name]).trim() !== "");

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto py-10 sm:py-14 pb-28">
      <Link to={`/documente/${slug}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 mb-6">
        <ArrowLeft className="w-4 h-4" /> Înapoi
      </Link>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2].map((s) => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full ${step >= s ? "bg-blue-600" : "bg-slate-200 dark:bg-slate-700"}`} />
            <p className={`mt-2 text-xs font-semibold ${step >= s ? "text-blue-600" : "text-slate-400"}`}>
              Pasul {s} din 2: {s === 1 ? "Completează" : "Verifică & Plată"}
            </p>
          </div>
        ))}
      </div>

      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-6">
        {doc.title}
      </h1>

      {step === 1 && (
        <div className="space-y-5 animate-fade-up">
          {doc.fields.map((f) => (
            <DynamicField
              key={f.name}
              field={f}
              value={values[f.name]}
              onChange={onChange}
              error={errors[f.name]}
            />
          ))}
          <Button
            data-testid="continue-to-review-btn"
            onClick={toReview}
            className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12 text-base mt-4"
          >
            Continuă la verificare
            <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="animate-fade-up">
          {/* Document preview (deterministic, before payment) */}
          <div
            className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
            data-testid="document-preview"
          >
            <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Previzualizare document
              </span>
              <Badge variant="secondary" className="ml-auto text-[10px]">Model</Badge>
            </div>
            <div className="relative p-6 sm:p-8 min-h-[220px]">
              {previewLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : preview ? (
                <DocPreview s={preview} />
              ) : (
                <p className="text-sm text-slate-400 text-center py-10">
                  Previzualizarea nu este disponibilă momentan.
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-blue-50 dark:bg-blue-950/40 text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
              Aceasta este o previzualizare pe baza datelor tale. După plată, textul este
              formulat profesional și primești documentul final în format PDF și Word.
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg text-slate-900 dark:text-white">
                Verifică informațiile
              </h2>
              <Button
                data-testid="edit-data-btn"
                variant="ghost"
                size="sm"
                onClick={() => setStep(1)}
                className="text-blue-600 hover:text-blue-700"
              >
                <Pencil className="w-4 h-4 mr-1" /> Modifică
              </Button>
            </div>
            <dl className="divide-y divide-slate-100 dark:divide-slate-800">
              {filledFields.map((f) => (
                <div key={f.name} className="py-3 grid grid-cols-3 gap-3" data-testid={`review-${f.name}`}>
                  <dt className="text-sm text-slate-500 col-span-1">{f.label}</dt>
                  <dd className="text-sm font-medium text-slate-900 dark:text-white col-span-2 break-words whitespace-pre-wrap">
                    {String(values[f.name])}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <label className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900 cursor-pointer">
            <Checkbox
              data-testid="agree-checkbox"
              checked={agree}
              onCheckedChange={setAgree}
              className="mt-0.5"
            />
            <span className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
              Am verificat informațiile introduse și sunt de acord cu{" "}
              <Link to="/termeni" className="text-blue-600 underline" target="_blank">
                Termenii
              </Link>{" "}
              și{" "}
              <Link to="/confidentialitate" className="text-blue-600 underline" target="_blank">
                Politica de confidențialitate
              </Link>
              .
            </span>
          </label>

          <div className="mt-6 flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <span className="text-sm text-slate-600 dark:text-slate-300">Total de plată</span>
            <span className="font-heading text-2xl font-extrabold text-slate-900 dark:text-white">
              {RON(doc.price)}
            </span>
          </div>

          <Button
            data-testid="pay-btn"
            onClick={pay}
            disabled={submitting}
            className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-14 text-base mt-4"
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Se redirecționează...</>
            ) : (
              <><Lock className="w-4 h-4 mr-2" /> Plătește {RON(doc.price)}</>
            )}
          </Button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck className="w-4 h-4" /> Plată securizată prin Stripe. Nu stocăm datele cardului.
          </p>
        </div>
      )}
    </div>
  );
}
